import { useEffect, useMemo, useRef, useState } from 'react'
import { QRCodeCanvas } from 'qrcode.react'
import { watchActiveDiningField, lookupByLastName, sendDiningMessage, teamLabel, playersOf, watchScheduledDiningReminders } from './diningSend.js'
import { DINING_LINES, nextRotatingLine } from './diningLines.js'

// PBCC Dining Staff — a STANDALONE, walled-off tool. Reads team names from the
// active field and sends "table is ready" to players' phones via the separate
// diningMessages pipeline. No PIN (staff never reach the pro-shop side), no admin
// imports, no links out. This is the entire dining surface.
const GREEN = '#123D26'
const GOLD = '#C2A24E'
const CREAM = '#F7F2E6'

export default function DiningApp() {
  const [field, setField] = useState({ eid: null, eventName: '', teams: [] })
  const [tab, setTab] = useState('single') // 'single' (one) | 'group' (a visible list)
  const [query, setQuery] = useState('')
  const [single, setSingle] = useState(null) // tab 1: the ONE picked team/player
  const [selected, setSelected] = useState([]) // tab 2: the group list {key, kind, teamId, ...}
  const [lineMode, setLineMode] = useState('rotate') // 'rotate' | 'manual'
  const [manualIdx, setManualIdx] = useState(0)
  const [listening, setListening] = useState(false)
  const [micNote, setMicNote] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState('')
  const [entered, setEntered] = useState(false) // QR-only gate shown first, every open
  const [qrPng, setQrPng] = useState('') // real PNG data-URL so long-press = Save to Photos
  const [reminders, setReminders] = useState([]) // read-only scheduled dinner reminders
  const [showReminders, setShowReminders] = useState(false)
  const recRef = useRef(null)

  useEffect(() => watchActiveDiningField(setField), [])
  useEffect(() => watchScheduledDiningReminders(setReminders), [])

  const diningUrl = typeof window !== 'undefined' ? `${window.location.origin}/dining` : '/dining'

  // Read the off-screen QR canvas into a PNG data-URL. Long-press "Save to Photos"
  // (iOS) / "Download image" (Android) only works on a real <img>/raster — never
  // on a canvas or SVG — so the gate shows this PNG, not the canvas.
  useEffect(() => {
    const t = setTimeout(() => {
      const c = document.querySelector('[data-dining-qr] canvas')
      if (c && c.toDataURL) { try { setQrPng(c.toDataURL('image/png')) } catch { /* ignore */ } }
    }, 90)
    return () => clearTimeout(t)
  }, [diningUrl, entered])
  const results = useMemo(() => lookupByLastName(field.teams, query), [field.teams, query])

  const targetForTeam = (t) => ({ key: `team:${t.teamId}`, kind: 'team', teamId: t.teamId, teamName: teamLabel(t), label: teamLabel(t) })
  const targetForPlayer = (t, player) => ({ key: `player:${t.teamId}:${player}`, kind: 'player', teamId: t.teamId, player, label: `${player} (solo)` })
  // In SINGLE tab a pick replaces the one selection; in GROUP tab it adds to the list.
  const pick = (tgt) => {
    if (tab === 'single') { setSingle(tgt); setSent('') }
    else { setSelected((s) => (s.some((x) => x.key === tgt.key) ? s : [...s, tgt])); setSent('') }
  }
  const removeTarget = (key) => setSelected((s) => s.filter((x) => x.key !== key))

  // Real voice-to-text via the browser Web Speech API (works on the dining WEB app).
  const startMic = () => {
    const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition)
    if (!SR) { setMicNote('Voice isn’t supported on this browser — please type the name.'); return }
    try {
      const rec = new SR()
      rec.lang = 'en-US'; rec.interimResults = false; rec.maxAlternatives = 1
      rec.onresult = (e) => { const txt = e.results?.[0]?.[0]?.transcript || ''; setQuery(txt) }
      rec.onerror = () => { setListening(false); setMicNote('Didn’t catch that — try again or type it.') }
      rec.onend = () => setListening(false)
      recRef.current = rec
      setMicNote(''); setListening(true); rec.start()
    } catch { setListening(false); setMicNote('Voice couldn’t start — please type the name.') }
  }
  const stopMic = () => { try { recRef.current?.stop() } catch { /* ignore */ } setListening(false) }

  const send = async () => {
    const list = tab === 'single' ? (single ? [single] : []) : selected
    if (!field.eid || list.length === 0 || sending) return
    setSending(true); setSent('')
    try {
      const text = lineMode === 'rotate' ? nextRotatingLine() : DINING_LINES[manualIdx]
      const targets = list.map((s) => (s.kind === 'team'
        ? { kind: 'team', teamId: s.teamId, teamName: s.teamName }
        : { kind: 'player', teamId: s.teamId, player: s.player }))
      await sendDiningMessage(field.eid, text, targets)
      setSent(`✓ Sent to ${list.length} ${list.length === 1 ? 'table' : 'tables'}: “${text}”`)
      setSingle(null); setSelected([]); setQuery('')
    } catch (e) { setSent(e?.message || 'Send failed — try again.') }
    setSending(false)
  }

  // FIRST SCREEN — QR only, big and front-and-center, with just the save
  // instruction. Nothing else: no search, mic, tray, send, or delete/remove.
  // Shown on every open; "Continue" leads to the actual dining tool.
  if (!entered) {
    return (
      <div style={{ minHeight: '100vh', background: GREEN, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 18px', fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif' }}>
        <div style={{ background: CREAM, borderRadius: 24, border: `3px solid ${GOLD}`, padding: 24, textAlign: 'center', maxWidth: 380, width: '100%' }}>
          {/* Off-screen canvas → read to PNG. The visible QR is a real <img> so
              long-press gives the native Save to Photos / Download image. */}
          <div data-dining-qr style={{ position: 'absolute', left: -99999, top: 0 }} aria-hidden="true">
            <QRCodeCanvas value={diningUrl} size={512} bgColor="#FFFFFF" fgColor={GREEN} includeMargin />
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
            {qrPng
              ? <img src={qrPng} alt="Dining tool QR code — tap and hold to save" width={260} height={260} style={{ width: 260, height: 260, display: 'block' }} />
              : <div style={{ width: 260, height: 260 }} />}
          </div>
          <div style={{ color: GREEN, fontWeight: 800, fontSize: 17, lineHeight: 1.45 }}>
            Tap and hold this code to save it to your Photos — if you lose this page, scan it again to get back in.
          </div>
        </div>
        <button
          onClick={() => setEntered(true)}
          style={{ marginTop: 24, background: GOLD, color: GREEN, border: 'none', borderRadius: 999, padding: '15px 44px', fontWeight: 900, fontSize: 18 }}>
          Continue →
        </button>
      </div>
    )
  }

  const chosenCount = tab === 'single' ? (single ? 1 : 0) : selected.length
  return (
    <div style={{ minHeight: '100vh', background: GREEN, padding: '16px 12px', fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif' }}>
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 14 }}>
          <div style={{ color: GOLD, fontWeight: 800, letterSpacing: 3, fontSize: 22 }}>PBCC DINING</div>
          <div style={{ color: CREAM, fontWeight: 900, fontSize: 26, marginTop: 2 }}>Table Ready</div>
          <div style={{ color: '#D8CFB8', fontSize: 13, marginTop: 4 }}>
            {field.eventName ? field.eventName : 'Waiting for the active field…'}
          </div>
        </div>

        {/* Two tabs — single vs group */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {[['single', 'Team or Player'], ['group', 'Teams or Players']].map(([k, lbl]) => (
            <button key={k} onClick={() => { setTab(k); setSent('') }}
              style={{ flex: 1, background: tab === k ? CREAM : 'transparent', color: tab === k ? GREEN : CREAM, border: `2px solid ${tab === k ? CREAM : '#3A5A46'}`, borderRadius: 12, padding: '11px 8px', fontWeight: 900, fontSize: 15 }}>
              {lbl}
            </button>
          ))}
        </div>
        <div style={{ color: '#D8CFB8', fontSize: 12, textAlign: 'center', marginBottom: 12 }}>
          {tab === 'single' ? 'Pick ONE team or player, then send.' : 'Build a list of tables, then send to all at once.'}
        </div>

        {/* Search + mic */}
        <div style={{ background: CREAM, borderRadius: 16, padding: 14, marginBottom: 14 }}>
          <div style={{ fontWeight: 800, color: GREEN, fontSize: 13, marginBottom: 6 }}>Find a table — type or say a last name</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. Ratliff"
              style={{ flex: 1, border: '1.5px solid #D1D5DB', borderRadius: 10, padding: '10px 12px', fontSize: 16, color: '#111827' }}
            />
            <button
              onClick={listening ? stopMic : startMic}
              style={{ background: listening ? '#B91C1C' : GREEN, color: '#fff', border: 'none', borderRadius: 10, padding: '0 14px', fontSize: 20, fontWeight: 800 }}
              title="Say a last name">
              {listening ? '■' : '🎤'}
            </button>
          </div>
          {micNote ? <div style={{ color: '#B45309', fontSize: 12, marginTop: 6, fontWeight: 700 }}>{micNote}</div> : null}
          {listening ? <div style={{ color: GREEN, fontSize: 12, marginTop: 6, fontWeight: 700 }}>Listening… say the last name.</div> : null}

          {/* Results */}
          {query.trim() ? (
            <div style={{ marginTop: 10 }}>
              {results.length === 0 ? (
                <div style={{ color: '#6B7280', fontStyle: 'italic', fontSize: 14 }}>No player found for “{query.trim()}”.</div>
              ) : results.map((t) => (
                <div key={t.teamId} style={{ borderTop: '1px solid #E5E7EB', padding: '8px 0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontWeight: 800, color: GREEN }}>{teamLabel(t)}</div>
                    <div style={{ fontSize: 11, color: '#6B7280' }}>#{t.teamName}</div>
                  </div>
                  <button onClick={() => pick(targetForTeam(t))} style={{ background: GREEN, color: '#fff', border: 'none', borderRadius: 999, padding: '7px 12px', fontWeight: 800, fontSize: 13 }}>{tab === 'single' ? 'Team' : '+ Team'}</button>
                  {playersOf(t).map((p) => (
                    <button key={p} onClick={() => pick(targetForPlayer(t, p))} style={{ background: '#fff', color: GREEN, border: `1.5px solid ${GREEN}`, borderRadius: 999, padding: '6px 10px', fontWeight: 700, fontSize: 12 }}>{tab === 'single' ? '' : '+ '}{p.split(/\s+/)[0]}</button>
                  ))}
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* Selection — tab 1: the one pick · tab 2: a visible removable list */}
        {tab === 'single' ? (
          <div style={{ background: CREAM, borderRadius: 16, padding: 14, marginBottom: 14 }}>
            <div style={{ fontWeight: 800, color: GREEN, fontSize: 13, marginBottom: 8 }}>Selected</div>
            {!single ? (
              <div style={{ color: '#6B7280', fontStyle: 'italic', fontSize: 14 }}>Pick a team or player above.</div>
            ) : (
              <span style={{ background: GREEN, color: CREAM, borderRadius: 999, padding: '8px 14px', fontWeight: 800, fontSize: 15, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {single.label}
                <button onClick={() => setSingle(null)} title="Clear" style={{ background: 'transparent', border: 'none', color: GOLD, fontWeight: 900, fontSize: 16 }}>×</button>
              </span>
            )}
          </div>
        ) : (
          <div style={{ background: CREAM, borderRadius: 16, padding: 14, marginBottom: 14 }}>
            <div style={{ fontWeight: 800, color: GREEN, fontSize: 13, marginBottom: 8 }}>Ready tables ({selected.length})</div>
            {selected.length === 0 ? (
              <div style={{ color: '#6B7280', fontStyle: 'italic', fontSize: 14 }}>Add teams or players above — they’ll appear here. Send them all at once.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {selected.map((s) => (
                  <div key={s.key} style={{ background: GREEN, color: CREAM, borderRadius: 12, padding: '10px 14px', fontWeight: 800, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{s.label}</span>
                    <button onClick={() => removeTarget(s.key)} title="Remove" style={{ background: 'transparent', border: `1.5px solid ${GOLD}`, color: GOLD, fontWeight: 900, fontSize: 15, borderRadius: 999, width: 30, height: 30, lineHeight: 1 }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Line + send */}
        <div style={{ background: CREAM, borderRadius: 16, padding: 14, marginBottom: 14 }}>
          <div style={{ fontWeight: 800, color: GREEN, fontSize: 13, marginBottom: 8 }}>Message</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <button onClick={() => setLineMode('rotate')} style={{ flex: 1, background: lineMode === 'rotate' ? GREEN : '#fff', color: lineMode === 'rotate' ? '#fff' : GREEN, border: `1.5px solid ${GREEN}`, borderRadius: 10, padding: '8px', fontWeight: 800, fontSize: 13 }}>Rotate automatically</button>
            <button onClick={() => setLineMode('manual')} style={{ flex: 1, background: lineMode === 'manual' ? GREEN : '#fff', color: lineMode === 'manual' ? '#fff' : GREEN, border: `1.5px solid ${GREEN}`, borderRadius: 10, padding: '8px', fontWeight: 800, fontSize: 13 }}>Pick a line</button>
          </div>
          {lineMode === 'manual' ? (
            <div style={{ display: 'grid', gap: 6 }}>
              {DINING_LINES.map((l, i) => (
                <button key={i} onClick={() => setManualIdx(i)} style={{ textAlign: 'left', background: manualIdx === i ? '#ECFDF5' : '#fff', border: `1.5px solid ${manualIdx === i ? GREEN : '#D1D5DB'}`, borderRadius: 10, padding: '8px 10px', color: '#111827', fontSize: 13 }}>{l}</button>
              ))}
            </div>
          ) : (
            <div style={{ color: '#6B7280', fontSize: 13, fontStyle: 'italic' }}>A different cowboy line each send (auto-rotates through all 6).</div>
          )}
        </div>

        <button
          onClick={send}
          disabled={!field.eid || chosenCount === 0 || sending}
          style={{ width: '100%', background: GOLD, color: GREEN, border: 'none', borderRadius: 14, padding: '16px', fontWeight: 900, fontSize: 18, opacity: (!field.eid || chosenCount === 0 || sending) ? 0.5 : 1 }}>
          {sending ? 'Sending…' : (tab === 'single'
            ? (single ? `🤠 Send “Table Ready” to ${single.label}` : '🤠 Send “Table Ready”')
            : `🤠 Send “Table Ready” to ${chosenCount} ${chosenCount === 1 ? 'table' : 'tables'}`)}
        </button>
        {sent ? <div style={{ color: CREAM, fontWeight: 800, textAlign: 'center', marginTop: 12 }}>{sent}</div> : null}

        {/* READ-ONLY: the auto-scheduled dinner reminders. Viewing NEVER sends. */}
        {reminders.length > 0 ? (
          <div style={{ marginTop: 20 }}>
            <button onClick={() => setShowReminders((s) => !s)}
              style={{ width: '100%', background: 'transparent', color: GOLD, border: `1.5px solid ${GOLD}`, borderRadius: 12, padding: '12px', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
              🔔 Dinner Reminders — {reminders.filter((r) => r.fired && !r.expired).length}/{reminders.length} sent {showReminders ? '▲' : '▼'}
            </button>
            {showReminders ? (
              <div style={{ background: CREAM, borderRadius: 16, padding: 14, marginTop: 10 }}>
                <div style={{ color: '#6B7280', fontSize: 11, marginBottom: 8, fontWeight: 700 }}>
                  Auto-scheduled · view only (viewing never sends). Each resolves its wave live at fire time.
                </div>
                {reminders.map((r) => {
                  const ct = r.fireAtMs
                    ? new Date(Number(r.fireAtMs)).toLocaleString('en-US', { timeZone: 'America/Chicago', weekday: 'short', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' CT'
                    : '—'
                  const wave = Number(r.waveHour) === 8 ? 'Wave 1 (8 AM)' : Number(r.waveHour) === 13 ? 'Wave 2 (1 PM)' : `Wave @${r.waveHour}`
                  const status = r.expired ? 'dropped (too late)' : r.fired ? `✓ sent to ${r.recipients ?? '?'} teams${r.waveKey ? ' · ' + r.waveKey : ''}` : 'scheduled'
                  const statusColor = r.expired ? '#B91C1C' : r.fired ? '#166534' : '#92400E'
                  return (
                    <div key={r.id} style={{ borderTop: '1px solid #E5E7EB', padding: '9px 0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontWeight: 800, color: GREEN, fontSize: 13 }}>{ct}</span>
                        <span style={{ fontWeight: 800, color: GREEN, fontSize: 12, background: '#ECFDF5', borderRadius: 999, padding: '2px 9px' }}>{wave}</span>
                      </div>
                      <div style={{ color: '#111827', fontSize: 13, marginTop: 4 }}>“{r.text}”</div>
                      <div style={{ color: statusColor, fontSize: 11, fontWeight: 800, marginTop: 3 }}>{status}</div>
                    </div>
                  )
                })}
              </div>
            ) : null}
          </div>
        ) : null}

        <div style={{ color: '#6B7280', fontSize: 11, textAlign: 'center', marginTop: 16 }}>Dining staff tool · sends to players’ phones only · nothing else here.</div>
      </div>
    </div>
  )
}
